// #2704 -- the ONE thing announcer-fe persists between loads: the class list's
// identity and order, never anything derived from rides.
//
// Why only this: classes (and which rings exist) barely change during a show,
// and AnnouncerContext cannot pick a ring, so nothing ring-scoped (/ride,
// /break, /ring-state, results summary, presence, statuses, sheets, tests) can
// start, until the heavy GET /class answers. Seeding the ring list from this
// cache lets those start on the first render while the live /class revalidates.
//
// Why a whitelist projection and not the raw rows: GET /class rows carry
// ride-derived values (the read-time scheduledTime derived from rides,
// fvdPushState aggregated over released rides, testDisagreement, sponsor).
// Those go stale the moment they are cached, even though they look like cheap
// static class fields. Only CLASS_CACHE_FIELDS are ever written, so a ride-
// derived field added to the API later is excluded by default. Rides, statuses,
// score sheets, ring state and results are never cached anywhere (#2190).
const STORAGE_KEY = "announcer-fe.classListCache";

export const CLASS_CACHE_FIELDS = ["id", "showId", "ring", "classNumber", "className"];
// A show runs days, not weeks; an older entry is a different event's list.
export const CLASS_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Bound storage: keep the most recently saved shows only.
export const CLASS_CACHE_MAX_SHOWS = 5;

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function projectClass(cls) {
  const out = {};
  for (const field of CLASS_CACHE_FIELDS) {
    if (cls?.[field] !== undefined) out[field] = cls[field];
  }
  return out;
}

export function readClassCache(showId, now = Date.now()) {
  if (!showId) return null;
  const entry = readAll()[showId];
  if (!entry || !Array.isArray(entry.classes) || !entry.classes.length) return null;
  if (typeof entry.savedAt !== "number" || now - entry.savedAt > CLASS_CACHE_MAX_AGE_MS) return null;
  return entry.classes;
}

export function writeClassCache(showId, classes, now = Date.now()) {
  if (!showId || !Array.isArray(classes) || !classes.length) return;
  try {
    const all = readAll();
    all[showId] = { savedAt: now, classes: classes.map(projectClass) };
    const newest = Object.entries(all)
      .sort(([, a], [, b]) => (b?.savedAt ?? 0) - (a?.savedAt ?? 0))
      .slice(0, CLASS_CACHE_MAX_SHOWS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(newest)));
  } catch {
    // Storage unavailable or full (private browsing): the cache is an
    // optimisation, never required.
  }
}
